import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { useAuthModalStore } from '../stores/useAuthModalStore';
import { useGalleryStore } from '../stores/useGalleryStore';
import { useGenerationStore } from '../stores/useGenerationStore';
import { GalleryItem, StyleDetails } from '../types/gallery';
import aiService from '../services/aiService';
import { mediaService } from '../services/mediaService';
import { savePendingGeneration } from './usePendingGeneration';
import { APP_HOME } from '../utils/segment';

export const useHomeGeneration = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { openModal: openAuthModal } = useAuthModalStore();
  const { addItem } = useGalleryStore();
  const { setHomePageResult, setIsLoading, setError, setPrompt } = useGenerationStore();

  const handleGenerate = useCallback(async (filesOrFile: File[] | File, generationPrompt: string, styleDetails: StyleDetails, aspectRatio: string, _resolution?: string) => {
    const files = Array.isArray(filesOrFile) ? filesOrFile : (filesOrFile ? [filesOrFile] : []);

    // Check authentication before generating
    if (!isAuthenticated) {
      // Save pending generation data to sessionStorage before redirect
      // (savePendingGeneration only persists a single file — pick the first to preserve legacy behavior)
      savePendingGeneration(files[0] ?? null, generationPrompt, styleDetails, aspectRatio);
      openAuthModal({
        pendingAction: () => handleGenerate(files, generationPrompt, styleDetails, aspectRatio),
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setHomePageResult(null);

    try {
      let resultTask;
      const fullPrompt = styleDetails.stylePrompt ? `${generationPrompt} ${styleDetails.stylePrompt}` : generationPrompt;

      if (files.length > 0) {
        // Clothing generation from home "Enviar imagem" — supports 1..N images
        const response = await aiService.generateClothing(files, fullPrompt, {
          size: aspectRatio,
          quality: 'high',
          flow: 'home'
        });
        resultTask = await aiService.pollTask(response.taskId);
      } else {
        // Text to image
        const response = await aiService.generateImage(fullPrompt, aspectRatio);
        resultTask = await aiService.pollTask(response.taskId);
      }

      if (resultTask && (resultTask.status === 'completed' || resultTask.status === 'image_generated')) {
        // Get image URL
        const imageUrl = resultTask.images && resultTask.images.length > 0 ? resultTask.images[0].url : null;

        if (imageUrl) {
          const newImage: GalleryItem = {
            id: `img_${resultTask.id}`,
            backendId: resultTask.images && resultTask.images.length > 0 ? resultTask.images[0].id : undefined,
            type: 'image',
            src: imageUrl,
            prompt: fullPrompt,
            createdAt: resultTask.createdAt || new Date().toISOString(),
          };
          addItem(newImage);
          setHomePageResult(newImage);
        } else {
          setError('Imagem gerada não encontrada na resposta.');
        }
      } else {
        setError('Falha ao gerar imagem: ' + (resultTask?.errorMessage || 'Erro desconhecido'));
      }
    } catch (e: any) {
      console.error(e);
      setError('Ocorreu um erro ao gerar a imagem: ' + (e.message || 'Verifique o console.'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, openAuthModal, addItem, setHomePageResult, setIsLoading, setError]);

  const handleSelectStyle = useCallback((stylePrompt: string) => {
    setPrompt(stylePrompt);
    navigate(APP_HOME);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  }, [navigate, setPrompt]);

  const handleDownload = useCallback((id: string, filename: string) => {
    // Extract numeric ID from "img_123" or "vid_123"
    const numericId = id.replace(/^(img_|vid_)/, '');
    const isVideo = id.startsWith('vid_');

    const type = isVideo ? 'video' : 'image';

    mediaService.downloadMedia(numericId, type)
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(err => {
        console.error("Download error:", err);
        // Fallback or error notification
        alert("Erro ao baixar arquivo. Tente novamente.");
      });
  }, []);

  const handleVideoGenerated = useCallback((videoUrl: string, prompt: string, posterItem: GalleryItem) => {
    if (posterItem.type !== 'image') return;
    const newVideo: GalleryItem = {
      id: `vid_${Date.now()}`,
      type: 'video',
      src: videoUrl,
      poster: posterItem.src,
      prompt: prompt,
      createdAt: new Date().toISOString(),
    };
    addItem(newVideo);
    setHomePageResult(newVideo);
    navigate(APP_HOME);
  }, [addItem, setHomePageResult, navigate]);

  return {
    handleGenerate,
    handleSelectStyle,
    handleDownload,
    handleVideoGenerated,
  };
};
